import { create } from "zustand";
import type { ViewState } from "../../../lib/canvas-coordinates";
import { loadGridSnapPreference, saveGridSnapPreference } from "../../../lib/grid-snap-preference";
import { loadPenColor, type PenColor } from "../../../lib/pen-preference";
import { loadStickyColor, type StickyColor } from "../../../lib/sticky-preference";

// キャンバス UI のローカル状態を管理する Zustand store。
// pan/zoom、選択中ツール、選択中オブジェクトなど高頻度更新の値を保持する。

export type CanvasTool =
  | "idle"
  | "panning"
  | "place-sticky"
  | "place-shape"
  | "place-text"
  | "draw-pen";

export type ShapeKind = "circle" | "rectangle" | "arrow";

export interface PointerPosition {
  readonly x: number;
  readonly y: number;
}

/** 右クリックで開くコンテキストメニューの状態（クライアント座標）。 */
export interface ContextMenuState {
  readonly objectId: string;
  readonly clientX: number;
  readonly clientY: number;
}

interface CanvasUiState {
  view: ViewState;
  tool: CanvasTool;
  shapeKind: ShapeKind;
  penColor: PenColor;
  stickyColor: StickyColor;
  selectedObjectIds: readonly string[];
  lastPointerStagePosition: PointerPosition | null;
  /** グリッド吸着モード（ON のとき、ペン以外のオブジェクトは確定時に CANVAS_SNAP_PX へ丸める）。 */
  gridSnapEnabled: boolean;
  contextMenu: ContextMenuState | null;

  setView: (view: ViewState) => void;
  setTool: (tool: CanvasTool) => void;
  setShapeKind: (shape: ShapeKind) => void;
  setPenColor: (color: PenColor) => void;
  setStickyColor: (color: StickyColor) => void;
  setSelectedObjectIds: (ids: readonly string[]) => void;
  setLastPointerStagePosition: (point: PointerPosition | null) => void;
  setGridSnapEnabled: (enabled: boolean) => void;
  openContextMenu: (state: ContextMenuState) => void;
  closeContextMenu: () => void;
  resetMode: () => void;
}

export const useCanvasStore = create<CanvasUiState>((set) => ({
  view: { panX: 0, panY: 0, zoom: 1 },
  tool: "idle",
  shapeKind: "rectangle",
  penColor: loadPenColor(),
  stickyColor: loadStickyColor(),
  selectedObjectIds: [],
  lastPointerStagePosition: null,
  gridSnapEnabled: loadGridSnapPreference(),
  contextMenu: null,

  setView: (view) => set({ view }),
  setTool: (tool) => set({ tool }),
  setShapeKind: (shapeKind) => set({ shapeKind }),
  setPenColor: (penColor) => set({ penColor }),
  setStickyColor: (stickyColor) => set({ stickyColor }),
  setSelectedObjectIds: (selectedObjectIds) => set({ selectedObjectIds }),
  setLastPointerStagePosition: (lastPointerStagePosition) => set({ lastPointerStagePosition }),
  setGridSnapEnabled: (enabled) => {
    saveGridSnapPreference(enabled);
    set({ gridSnapEnabled: enabled });
  },
  openContextMenu: (contextMenu) => set({ contextMenu }),
  closeContextMenu: () => set({ contextMenu: null }),
  resetMode: () => set({ tool: "idle", selectedObjectIds: [], contextMenu: null }),
}));
